export type DesktopEnvironment = 'production' | 'beta' | 'staging' | 'develop';

export interface DesktopEnvironmentIdentity {
    environment: DesktopEnvironment;
    displayName: string;
    appId: string;
    installerName: string;
    deepLinkScheme: string;
    outlineColor?: string;
    legacyDeepLinkSchemes?: string[];
    /** The production app keeps its existing Electron user-data location. */
    userDataDirectory?: string;
}

const DESKTOP_ENVIRONMENTS: Record<DesktopEnvironment, DesktopEnvironmentIdentity> = {
    production: {
        environment: 'production',
        displayName: 'Cephable',
        // Keep this identifier stable so production upgrades retain their install, updates, and local data.
        appId: 'com.enabledplay.desktop',
        installerName: 'enabled_play',
        deepLinkScheme: 'cephable',
        legacyDeepLinkSchemes: ['enabledplay'],
    },
    beta: {
        environment: 'beta',
        displayName: 'Cephable Beta',
        appId: 'com.enabledplay.desktop.beta',
        installerName: 'enabled_play_beta',
        deepLinkScheme: 'cephable-beta',
        outlineColor: '#7553D6',
        userDataDirectory: 'Cephable Beta',
    },
    staging: {
        environment: 'staging',
        displayName: 'Cephable Staging',
        appId: 'com.enabledplay.desktop.staging',
        installerName: 'enabled_play_staging',
        deepLinkScheme: 'cephable-staging',
        outlineColor: '#D66D16',
        userDataDirectory: 'Cephable Staging',
    },
    develop: {
        environment: 'develop',
        displayName: 'Cephable Develop',
        appId: 'com.enabledplay.desktop.develop',
        installerName: 'enabled_play_develop',
        deepLinkScheme: 'cephable-develop',
        outlineColor: '#007F71',
        legacyDeepLinkSchemes: ['cephable-dev'],
        userDataDirectory: 'Cephable Develop',
    },
};

/** Maps the legacy `dev` name to the user-facing `develop` desktop variant. */
export function getDesktopEnvironment(environment = process.env.ENABLED_ENVIRONMENT): DesktopEnvironment {
    switch (environment) {
        case 'dev':
        case 'develop':
            return 'develop';
        case 'beta':
        case 'staging':
            return environment;
        default:
            return 'production';
    }
}

export function getDesktopEnvironmentIdentity(environment?: string): DesktopEnvironmentIdentity {
    return DESKTOP_ENVIRONMENTS[getDesktopEnvironment(environment)];
}

export function getDesktopDeepLinkScheme(environment?: string): string {
    return getDesktopEnvironmentIdentity(environment).deepLinkScheme;
}

export function getDesktopDeepLinkSchemes(environment?: string): string[] {
    const identity = getDesktopEnvironmentIdentity(environment);
    return [identity.deepLinkScheme, ...(identity.legacyDeepLinkSchemes ?? [])];
}

/** The API selection intentionally differs from the install identity: Beta exercises production. */
export function getDesktopApiEnvironment(
    environment = process.env.ENABLED_ENVIRONMENT
): 'production' | 'staging' | 'dev' {
    switch (getDesktopEnvironment(environment)) {
        case 'develop':
            return 'dev';
        case 'staging':
            return 'staging';
        default:
            return 'production';
    }
}
